import React, { useState, useRef, useEffect } from 'react';

// --- 1. BASES DE DONNÉES LOCALES ---

const FORMATIONS = {
  dem: { name: "Délégué Médical", code: "DEM", level: "BAC + 2", price: "500.000 FCFA" },
  sem: { name: "Secrétariat Médical", code: "SEM", level: "1ère / Probatoire / OL", price: "350.000 FCFA" },
  vph: { name: "Vendeur en Pharmacie", code: "VPH", level: "Probatoire / OL", price: "350.000 FCFA" },
  atl: { name: "Aide Chimiste Biologiste", code: "ATL", level: "1ère / Probatoire / OL", price: "350.000 FCFA" },
  avs: { name: "Auxiliaire de Vie Sociale", code: "AVS", level: "3ème / BEPC / OL", price: "300.000 FCFA" },
  acm: { name: "Assistant en Cabinet Médical", code: "ACM", level: "3ème / BEPC", price: "350.000 FCFA" },
  tpd: { name: "Technicien Prothésiste Dentaire", code: "TPD", level: "BEPC", price: "400.000 FCFA" },
  acd: { name: "Assistant en Cabinet Dentaire", code: "ACD", level: "BEPC", price: "400.000 FCFA" }
};

const ADVANTAGES = {
  pedagogie: [
    "75 % pratique et 25 % théorie",
    "Approche par compétence (APC)",
    "Enseignants professionnels actifs sur le terrain"
  ],
  stages: [
    "Stage académique offert",
    "Immersion professionnelle garantie",
    "Accompagnement vers l'insertion"
  ],
  competences: [
    "Initiation à l'IA spécialisée",
    "Bureautique gratuite",
    "Anglais professionnel",
    "Entrepreneuriat"
  ]
};

// --- 2. TRAITEMENT DU LANGAGE NATUREL (NLP Local) ---

const normalizeText = (text) => {
  return text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
};

function detectFormation(input) {
  const text = normalizeText(input);

  for (const [key, formation] of Object.entries(FORMATIONS)) {
    if (text.includes(normalizeText(formation.name)) || text.includes(normalizeText(formation.code))) {
      return key;
    }
  }

  const aliases = {
    dem: ["delegue medical", "delegue", "commercial medical"],
    sem: ["secretaire medicale", "secretariat medical", "secretaire"],
    vph: ["vendeur pharmacie", "vendeuse pharmacie", "vendeur"],
    atl: ["laboratoire", "aide chimiste", "biologiste", "analyse"],
    avs: ["auxiliaire vie", "aide personne", "sociale"],
    acm: ["assistant cabinet medical", "assistant medical"],
    tpd: ["prothese dentaire", "prothesiste"],
    acd: ["assistant cabinet dentaire", "assistant dentaire"]
  };

  for (const [key, words] of Object.entries(aliases)) {
    if (words.some(word => text.includes(normalizeText(word)))) {
      return key;
    }
  }
  return null;
}

function detectRequests(input) {
  const text = normalizeText(input);
  const requests = [];

  if (/(prix|tarif|cout|frais|combien|scolarite)/.test(text)) requests.push("price");
  if (/(niveau|diplome|condition|admission|prerequis|bac|bepc|probatoire|ol)/.test(text)) requests.push("level");
  if (/(stage|immersion|emploi|insertion)/.test(text)) requests.push("stage");
  if (/(adresse|ou|situe|localisation|lieu|nkoabang|cef)/.test(text)) requests.push("location");
  if (/(whatsapp|contact|numero|appeler|joindre|inscription)/.test(text)) requests.push("contact");
  if (/(avantage|atout|pourquoi|pedagogie|pratique|ia|anglais|bureautique)/.test(text)) requests.push("advantages");
  if (/(formation|specialite|liste|filiere|proposez)/.test(text)) requests.push("list");

  return requests;
}

// --- 3. GÉNÉRATION DYNAMIQUE DES RÉPONSES ---

function answerFormationQuestion(formationKey, requests) {
  const formation = FORMATIONS[formationKey];
  let responseParts = [`🎓 ${formation.name} (${formation.code})`];

  if (requests.length === 0 || requests.includes("general")) {
    responseParts.push(`• Niveau requis : ${formation.level}\n• Scolarité : ${formation.price}\n• Stage académique offert\n\nSouhaitez-vous connaître notre procédure d'inscription ?`);
    return responseParts.join("\n\n");
  }

  if (requests.includes("level")) responseParts.push(`🎓 Niveau requis : ${formation.level}`);
  if (requests.includes("price")) responseParts.push(`💳 Scolarité : ${formation.price}`);
  if (requests.includes("stage")) responseParts.push(`🤝 Oui, la formation comprend un stage académique offert pour favoriser l'immersion professionnelle.`);
  if (requests.includes("location")) responseParts.push(`📍 La formation se déroule sur notre campus au Carrefour CEF, Nkoabang.`);
  if (requests.includes("contact")) responseParts.push(`📞 Pour vous inscrire, contactez-nous via WhatsApp : https://wa.me/message/EDWU2IBYEIZQJ1`);

  return responseParts.join("\n\n");
}

function answerGeneralQuestion(requests) {
  let responseParts = [];

  if (requests.includes("list")) {
    responseParts.push("🎓 Nos 8 spécialités paramédicales :\n1. Délégué Médical (DEM)\n2. Secrétariat Médical (SEM)\n3. Vendeur en Pharmacie (VPH)\n4. Aide Chimiste Biologiste (ATL)\n5. Auxiliaire de Vie Sociale (AVS)\n6. Assistant en Cabinet Médical (ACM)\n7. Technicien Prothésiste Dentaire (TPD)\n8. Assistant en Cabinet Dentaire (ACD)\n\n*Précisez une spécialité pour voir son tarif ou niveau !*");
  }
  
  if (requests.includes("advantages")) {
    responseParts.push(`🌟 Pourquoi choisir ITC Santé ?\n\nPédagogie :\n• ${ADVANTAGES.pedagogie.join("\n• ")}\n\nInnovations :\n• ${ADVANTAGES.competences.join("\n• ")}\n\nInsertion :\n• ${ADVANTAGES.stages.join("\n• ")}`);
  }

  if (requests.includes("price") && !requests.includes("list")) {
    responseParts.push("💳 Frais de scolarité globaux :\n• AVS : 300.000 FCFA\n• SEM, VPH, ATL, ACM : 350.000 FCFA\n• TPD, ACD : 400.000 FCFA\n• DEM : 500.000 FCFA");
  }

  if (requests.includes("location")) {
    responseParts.push("📍 Notre Adresse :\nITC Santé est situé au Carrefour CEF, Nkoabang, Yaoundé, Cameroun.");
  }

  if (requests.includes("contact")) {
    responseParts.push("📞 Contactez-nous directement sur WhatsApp :\n🏥 ITC Santé : https://wa.me/message/EDWU2IBYEIZQJ1\n🎓 ITC Formation : https://wa.me/c/237689223252");
  }

  return responseParts.length > 0 
    ? responseParts.join("\n\n") 
    : "🤔 Je veux être sûr de bien vous renseigner. Je peux vous informer sur :\n• Nos 8 Formations\n• Les Tarifs et Niveaux\n• Nos Avantages\n• Notre Localisation\nQue souhaitez-vous savoir ?";
}

function processUserQuery(input) {
  const formationKey = detectFormation(input);
  const requests = detectRequests(input);

  if (formationKey) {
    return answerFormationQuestion(formationKey, requests);
  } else {
    return answerGeneralQuestion(requests);
  }
}

// --- 4. COMPOSANT REACT UI ---

export default function FloatingAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { sender: 'bot', text: 'Bonjour ! 👋 Je suis l\'assistant d\'ITC Santé. Que souhaitez-vous savoir sur nos formations, tarifs ou conditions d\'admission ?' }
  ]);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = (textToSend = input) => {
    if (!textToSend.trim()) return;

    const userMessage = { sender: 'user', text: textToSend };
    const botResponse = { sender: 'bot', text: processUserQuery(textToSend) };

    setMessages((prev) => [...prev, userMessage, botResponse]);
    setInput('');
  };

  const quickChips = ['Nos Formations', 'Tarif Vendeur Pharmacie', 'Localisation', 'Avantages ITC'];

  return (
    <div className="fixed bottom-6 right-20 z-50 flex flex-col items-end font-sans">
      {isOpen && (
        <div className="mb-3 w-80 sm:w-96 h-[460px] bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden animate-fadeIn">
          <div className="bg-green-700 text-white p-3.5 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="w-2.5 h-2.5 bg-green-400 rounded-full animate-pulse"></span>
              <div>
                <h3 className="font-semibold text-sm m-0">Assistant ITC Santé</h3>
                <p className="text-[11px] text-green-100 m-0">En ligne | Réponses intelligentes</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-green-100 hover:text-white text-lg font-bold p-1">✕</button>
          </div>

          <div className="flex-1 p-3 overflow-y-auto space-y-3 bg-slate-50 text-sm">
            {messages.map((msg, index) => (
              <div key={index} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] p-3 rounded-xl whitespace-pre-line leading-relaxed ${
                    msg.sender === 'user' ? 'bg-green-600 text-white rounded-br-none' : 'bg-white text-gray-800 shadow-sm border border-gray-200 rounded-bl-none'
                  }`}>
                  {msg.text.split(/(https?:\/\/[^\s]+)/g).map((part, i) => 
                    part.match(/^https?:\/\//) ? (
                      <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-green-600 underline font-semibold break-all">{part}</a>
                    ) : (
                      <span key={i}>{part}</span>
                    )
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="px-3 py-2 bg-white border-t border-gray-100 flex gap-2 overflow-x-auto no-scrollbar">
            {quickChips.map((chip, idx) => (
              <button key={idx} onClick={() => handleSend(chip)} className="text-[11px] bg-green-50 text-green-700 border border-green-100 hover:bg-green-100 px-3 py-1.5 rounded-full whitespace-nowrap transition-colors">
                {chip}
              </button>
            ))}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="p-2.5 bg-white border-t border-gray-100 flex items-center space-x-2">
            <input type="text" placeholder="Posez votre question..." value={input} onChange={(e) => setInput(e.target.value)} className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-green-600 focus:ring-1 focus:ring-green-600 transition-all" />
            <button type="submit" className="bg-green-700 hover:bg-green-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">Envoyer</button>
          </form>
        </div>
      )}

      <div className="relative flex items-center">
        {!isOpen && (
          <div className="absolute right-16 bg-white text-green-800 text-xs font-bold px-3 py-2 rounded-lg shadow-lg border border-green-100 whitespace-nowrap animate-bounce flex items-center gap-2 cursor-pointer" onClick={() => setIsOpen(true)}>
            <span>Besoin d'aide ?</span>
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            <div className="absolute right-[-5px] top-1/2 transform -translate-y-1/2 w-2.5 h-2.5 bg-white rotate-45 border-r border-t border-green-100"></div>
          </div>
        )}
        <button onClick={() => setIsOpen(!isOpen)} className="w-14 h-14 bg-white border-2 border-green-700 hover:border-green-800 text-green-700 rounded-full shadow-2xl flex items-center justify-center transition-transform transform hover:scale-105" title="Discuter avec l'assistant">
          {isOpen ? <span className="text-xl font-bold">✕</span> : (
            <img src="/assistant-logo.png" alt="Assistant ITC" className="w-9 h-9 object-contain" />
          )}
        </button>
      </div>
    </div>
  );
}
